import { attachments, bucketEnum } from '@markku/db'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'

export type BucketType = (typeof bucketEnum.enumValues)[number]

// Generic metadata schema - entity-specific types can be defined in their own modules
export const attachmentMetadataSchema = z.record(z.any()).optional().nullable()

const selectAttachmentSchema = createSelectSchema(attachments)

export const insertAttachmentSchema = createInsertSchema(attachments).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})

export const updateAttachmentSchema = insertAttachmentSchema.partial().omit({
  entityType: true,
  entityId: true,
  storageKey: true,
  uploadedBy: true,
})

// Schema for creating attachment and getting presigned upload URL
export const createAttachmentSchema = z.object({
  bucket: z.enum(bucketEnum.enumValues).default('private'),
  filename: z.string().min(1),
  fileSize: z.number().int().positive(), // Client-provided metadata only - backend enforces its own max limit
  description: z.string().optional(),
  entityType: z.string().min(1).optional(), // Optional for unlinked uploads
  entityId: z.number().int().positive().optional(), // Optional for unlinked uploads
  metadata: attachmentMetadataSchema, // Add metadata support for creation
})

// Schema for linking attachment to entity
export const linkAttachmentSchema = z.object({
  id: z.number().int().positive(),
  entityType: z.string().min(1),
  entityId: z.number().int().positive(),
})

// Schema for getting attachments by entity
export const getAttachmentsByEntitySchema = z.object({
  entityType: z.string().min(1),
  entityId: z.number().int().positive(),
})

// Schema for file download/access
export const getAttachmentByIdSchema = z.object({
  id: z.number().int().positive(),
})

// Response schemas
export const createAttachmentResponseSchema = z.object({
  id: z.number(),
  storageKey: z.string(),
  uploadUrl: z.string(),
  fields: z.record(z.string()),
})

export const downloadUrlResponseSchema = z.object({
  url: z.string(),
})

// Standardized attachment response type that will be included in all entity responses
export const attachmentResponseSchema = selectAttachmentSchema.extend({
  metadata: attachmentMetadataSchema,
  // Presigned S3 URL (1 hour expiration). Optional because list endpoints can
  // skip URL generation for performance (see listOmitAttachmentDownloadUrl) and
  // only return attachment metadata; getById always populates it.
  downloadUrl: z.string().optional(),
})

// Generic helper to add optional attachments array to any Zod schema
export function withAttachments<T extends z.ZodRawShape>(
  schema: z.ZodObject<T>,
) {
  return schema.extend({
    attachments: z.array(attachmentResponseSchema).optional(),
    internalAttachments: z.array(attachmentResponseSchema).optional(),
  })
}

// Type exports
export type Attachment = z.infer<typeof selectAttachmentSchema>
export type AttachmentResponse = z.infer<typeof attachmentResponseSchema>
export type AttachmentMetadata = z.infer<typeof attachmentMetadataSchema>
export type CreateAttachment = z.infer<typeof insertAttachmentSchema>
export type UpdateAttachment = z.infer<typeof updateAttachmentSchema>
export type CreateAttachmentInput = z.infer<typeof createAttachmentSchema>
export type LinkAttachment = z.infer<typeof linkAttachmentSchema>
export type GetAttachmentsByEntity = z.infer<
  typeof getAttachmentsByEntitySchema
>
export type GetAttachmentById = z.infer<typeof getAttachmentByIdSchema>
export type CreateAttachmentResponse = z.infer<
  typeof createAttachmentResponseSchema
>
export type DownloadUrlResponse = z.infer<typeof downloadUrlResponseSchema>
